import { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import type { App_Users } from "@/src/gql/graphql";

// Import detail actions from EmployeeDetailConfig
import { EMPLOYEE_DETAIL_CONFIG } from "./EmployeeDetailConfig";

export const EMPLOYEE_CONFIG: SimpleTableConfig<App_Users> = {
  entity: "employees",
  displayName: "Employee",
  // No status select-type field on employees. Explicit opt-out — see SimpleTableConfigBase.statusFieldId.
  statusFieldId: null,
  defaultPageSize: 10,
  defaultSort: { field: "first_name", direction: "asc" },

  // Group detail-modal / mobile-sectioned fields into collapsible sections.
  mobileDetailSections: [
    { id: "profile", label: "Profile" },
    { id: "access", label: "Role & Access" },
    { id: "record", label: "Record" },
  ],
  mobileDetailUnsectionedLabel: "Other",

  /** Default table column order: who → email → role → status (audit fields trail) */
  tableColumnOrder: ["name", "email", "role_id", "is_active"],

  fields: {
    name: {
      type: "text",
      label: "Name",
      sortable: true,
      searchable: false, // Virtual field - does not exist in database, use first_name and last_name instead
      filterable: true,
      editable: false,
      width: 220,
      // Show row actions inline with this column
      showRowActions: true,
      mobileDetailSectionId: "profile",
    },
    email: {
      type: "text",
      label: "Email",
      sortable: true,
      searchable: true,
      filterable: true,
      editable: false,
      width: 260,
      mobileDetailSectionId: "profile",
    },
    role_id: {
      type: "text",
      label: "Role ID",
      isForeignKey: true,
      foreignKey: {
        referencedTable: "roles",
        referencedColumn: "id",
        constraintName: "app_users_role_id_fkey",
        onDelete: "RESTRICT",
        onUpdate: "NO ACTION",
        relationKind: "many-to-one",
      },
      sortable: true,
      searchable: false, // UUID field - not searchable with ilike
      filterable: true,
      editable: false,
      width: 160,
      groupable: true,
      mobileDetailSectionId: "access",
    },
    is_active: {
      type: "boolean",
      label: "Active",
      sortable: true,
      filterable: true,
      editable: true,
      width: 120,
      groupable: true,
      mobileDetailSectionId: "access",
    },
    created_at: {
      type: "datetime",
      label: "Created",
      sortable: true,
      filterable: true,
      editable: false, // Auto-generated timestamp
      width: 160,
      mobileDetailSectionId: "record",
    },
    updated_at: {
      type: "datetime",
      label: "Updated",
      sortable: true,
      filterable: true,
      editable: false, // Auto-generated timestamp
      width: 160,
      mobileDetailSectionId: "record",
    },
  },

  actions: {
    create: true,
    edit: true,
    delete: true,
    view: true,
  },

  // Add detail page actions for table rows
  detailActions: EMPLOYEE_DETAIL_CONFIG.actions,

  features: {
    search: true,
    filters: true,
    export: true,
    bulkActions: true,
    columnResizing: true,
    columnVisibility: true,
    savedViews: true,
    infiniteScroll: false,
    multiColumnSort: true,
    calendarView: true,
  },

  savedViews: [
    {
      id: "all-employees",
      name: "All Employees",
      filters: {},
      sort: { field: "first_name", direction: "asc" },
      visibleColumns: ["name", "email", "role_id", "is_active", "created_at"],
    },
    {
      id: "active-employees",
      name: "Active Employees",
      filters: { is_active: true },
      sort: { field: "first_name", direction: "asc" },
      visibleColumns: ["name", "email", "role_id", "is_active"],
    },
  ],

  // Custom filter hooks for saved views (can be populated later if needed)
  customFilterHooks: {},
};
